import { CATEGORY_LABELS } from '../constants/metricConstants'

const hasValue = (value) => value !== null && value !== undefined && value !== ''

const cents = (value) => Math.round(Number(value || 0) * 100) / 100
const centsOrNull = (value) => (hasValue(value) ? cents(value) : null)

const activeValues = (filterValue) => {
  if (filterValue?.mode !== 'custom') return []
  return Array.isArray(filterValue.values) ? filterValue.values : []
}

const uniqueOptions = (values, labelResolver = (value) => value) => {
  const seen = new Set()
  const options = []

  values.forEach((value) => {
    if (!value || seen.has(value)) return

    seen.add(value)
    options.push({
      value,
      label: labelResolver(value)
    })
  })

  return options
}

const childHasMetric = (child, key, fallbackKey) =>
  hasValue(child?.[key]) || Boolean(fallbackKey && hasValue(child?.[fallbackKey]))

const childMetricValue = (child, key, fallbackKey) => {
  const value = hasValue(child?.[key]) || !fallbackKey ? child?.[key] : child?.[fallbackKey]

  return Number(value || 0)
}

const sumChildMetric = (children, key, fallbackKey) => {
  if (!children.every((child) => childHasMetric(child, key, fallbackKey))) {
    return null
  }

  return children.reduce((total, child) => total + childMetricValue(child, key, fallbackKey), 0)
}

const filteredMetricValue = (children, key, fallbackKey) => sumChildMetric(children, key, fallbackKey)

const buildProductGroupFilteredRow = (row, children) => {
  const purchaseOrderTotal = centsOrNull(
    filteredMetricValue(children, 'purchase_order_total', 'total_oc')
  )
  const orderTotal = centsOrNull(filteredMetricValue(children, 'order_total', 'total_pedido'))
  const purchaseOrderLines = filteredMetricValue(children, 'purchase_order_lines')
  const orderLines = filteredMetricValue(children, 'order_lines')
  const salesLines = filteredMetricValue(children, 'sales_lines')
  const invoiceLines = filteredMetricValue(children, 'invoice_lines')

  return {
    ...row,
    children,
    purchase_order_counts: filteredMetricValue(children, 'purchase_order_counts'),
    purchase_order_total: purchaseOrderTotal,
    purchase_order_lines: purchaseOrderLines,
    order_counts: filteredMetricValue(children, 'order_counts'),
    order_total: orderTotal,
    order_lines: orderLines,
    fill_rate_order: calculateFillRatePercentage(orderLines, purchaseOrderLines),
    salesorder_total: centsOrNull(filteredMetricValue(children, 'salesorder_total')),
    sales_lines: salesLines,
    fill_rate_sales: calculateFillRatePercentage(salesLines, purchaseOrderLines),
    invoice_total: centsOrNull(filteredMetricValue(children, 'invoice_total')),
    invoice_lines: invoiceLines,
    fill_rate_invoice: calculateFillRatePercentage(invoiceLines, purchaseOrderLines),
    difference: calculateFillRateDifference(purchaseOrderTotal, orderTotal)
  }
}

export const isActiveFillRateFilter = (filterValue) => activeValues(filterValue).length > 0

export const resolveFillRateCategoryLabel = (value) => {
  if (!value) return '-'
  return CATEGORY_LABELS[value] || value
}

export const buildFillRateBuyerOptions = (summary = []) =>
  uniqueOptions(summary.map((row) => row?.buyer))

export const buildFillRateProductGroupOptions = (summary = []) =>
  uniqueOptions(
    summary.flatMap((row) =>
      Array.isArray(row?.children) ? row.children.map((child) => child?.name) : []
    ),
    resolveFillRateCategoryLabel
  )

export const filterFillRateSummary = (summary = [], { buyerFilter, productGroupFilter } = {}) => {
  const selectedBuyers = new Set(activeValues(buyerFilter))
  const selectedProductGroups = new Set(activeValues(productGroupFilter))
  const hasBuyerFilter = selectedBuyers.size > 0
  const hasProductGroupFilter = selectedProductGroups.size > 0

  return summary.reduce((filteredRows, row) => {
    const children = Array.isArray(row?.children) ? row.children : []
    const matchesBuyer = !hasBuyerFilter || selectedBuyers.has(row?.buyer)
    const matchingChildren = hasProductGroupFilter
      ? children.filter((child) => selectedProductGroups.has(child?.name))
      : children
    const matchesProductGroup = !hasProductGroupFilter || matchingChildren.length > 0

    if (!matchesBuyer || !matchesProductGroup) return filteredRows

    filteredRows.push({
      ...(hasProductGroupFilter ? buildProductGroupFilteredRow(row, matchingChildren) : row),
      children: matchingChildren
    })

    return filteredRows
  }, [])
}

export const calculateFillRateDifference = (purchaseOrderTotal, orderTotal) =>
  hasValue(purchaseOrderTotal) && hasValue(orderTotal)
    ? cents(cents(purchaseOrderTotal) - cents(orderTotal))
    : null

export const resolveFillRateDifference = (
  row,
  {
    differenceKey = 'difference',
    purchaseOrderKey = 'purchase_order_total',
    orderKey = 'order_total'
  } = {}
) => {
  const rawDifference = row?.[differenceKey]

  if (hasValue(rawDifference)) {
    const preparedDifference = Number(rawDifference)

    if (!Number.isNaN(preparedDifference)) return cents(preparedDifference)
  }

  return calculateFillRateDifference(row?.[purchaseOrderKey], row?.[orderKey])
}

export const getPositiveFillRateDifference = (purchaseOrderTotal, orderTotal) => {
  const difference = calculateFillRateDifference(purchaseOrderTotal, orderTotal)
  return difference > 0 ? difference : 0
}

export const getPositiveFillRateDifferenceValue = (value) => {
  const difference = cents(value)
  return difference > 0 ? difference : 0
}

export const hasPositiveFillRateDifference = (purchaseOrderTotal, orderTotal) =>
  getPositiveFillRateDifference(purchaseOrderTotal, orderTotal) > 0

export const calculateFillRatePercentage = (numerator, denominator) => {
  if (!hasValue(numerator) || !hasValue(denominator)) return null

  const validDenominator = Number(denominator || 0)
  if (validDenominator === 0) return 0

  return (Number(numerator || 0) / validDenominator) * 100
}

export const buildFillRateTotals = (summary = []) => {
  const totalFor = (key) => summary.reduce((total, row) => total + Number(row?.[key] || 0), 0)

  const purchaseOrderLines = totalFor('purchase_order_lines')
  const orderLines = totalFor('order_lines')
  const salesLines = totalFor('sales_lines')
  const invoiceLines = totalFor('invoice_lines')
  const difference = summary.reduce((total, row) => total + resolveFillRateDifference(row), 0)

  return {
    purchase_order_counts: totalFor('purchase_order_counts'),
    purchase_order_total: cents(totalFor('purchase_order_total')),
    purchase_order_lines: purchaseOrderLines,
    order_counts: totalFor('order_counts'),
    order_total: cents(totalFor('order_total')),
    order_lines: orderLines,
    fill_rate_order: calculateFillRatePercentage(orderLines, purchaseOrderLines),
    salesorder_total: cents(totalFor('salesorder_total')),
    sales_lines: salesLines,
    fill_rate_sales: calculateFillRatePercentage(salesLines, purchaseOrderLines),
    invoice_total: cents(totalFor('invoice_total')),
    invoice_lines: invoiceLines,
    fill_rate_invoice: calculateFillRatePercentage(invoiceLines, purchaseOrderLines),
    difference: cents(difference)
  }
}

export const buildFillRateDifferenceUrl = ({ startDate, endDate, buyer, productGroup }) => {
  const formatDate = (value) => (value?.format ? value.format('YYYY-MM-DD') : String(value || ''))

  const params = new URLSearchParams({
    start_date: formatDate(startDate),
    end_date: formatDate(endDate),
    buyer: buyer || '',
    product_group: productGroup || ''
  })

  return `/metricas_gerencia/productos_con_diferencias?${params.toString()}`
}

export const formatFillRateFilenameDate = (date = new Date()) => {
  const validDate = date instanceof Date ? date : new Date(date)
  const day = String(validDate.getDate()).padStart(2, '0')
  const month = String(validDate.getMonth() + 1).padStart(2, '0')

  return `${day}-${month}-${validDate.getFullYear()}`
}

export const buildFillRateExportFilename = (clientName, extension, date = new Date()) => {
  const normalizedExtension = extension.startsWith('.') ? extension : `.${extension}`

  return `FillRate_VistaResumida_${clientName || 'N/A'}_${formatFillRateFilenameDate(date)}${normalizedExtension}`
}

export const buildFillRateProductRows = (rows = []) =>
  rows
    .map((row) => {
      const difference = calculateFillRateDifference(row?.purchase_order_total, row?.order_total)

      return {
        ...row,
        product_code: row?.product_db_ref || row?.supplier_db_ref || '',
        product_barcode: row?.product_barcode || row?.barcode || '',
        description: row?.product_name || '',
        difference
      }
    })
    .sort((left, right) => {
      const leftHasDifference = left.difference !== 0
      const rightHasDifference = right.difference !== 0

      if (leftHasDifference !== rightHasDifference) {
        return leftHasDifference ? -1 : 1
      }

      if (leftHasDifference) {
        return Math.abs(right.difference) - Math.abs(left.difference)
      }

      return String(left.description).localeCompare(String(right.description))
    })

export const buildFillRateProductSummary = (rows = []) => {
  const enrichedRows = buildFillRateProductRows(rows)
  const rowsWithDifference = enrichedRows.filter((row) => row.difference !== 0)
  const purchaseOrderTotal = enrichedRows.reduce(
    (total, row) => total + Number(row?.purchase_order_total || 0),
    0
  )
  const orderTotal = enrichedRows.reduce((total, row) => total + Number(row?.order_total || 0), 0)

  return {
    rows: enrichedRows,
    productsWithDifferenceCount: rowsWithDifference.length,
    differenceTotal: calculateFillRateDifference(purchaseOrderTotal, orderTotal)
  }
}
